import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useDroppable } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { IconPlus, IconSettings } from "@tabler/icons-react"
import type { Column } from "../types"
import { TicketCard } from "./TicketCard"

interface BoardColumnProps {
  column: Column
  canEdit: boolean
  canAdminister: boolean
  onAddTicket: (column: Column) => void
  onEditColumn: (column: Column) => void
}

export function BoardColumn({ column, canEdit, canAdminister, onAddTicket, onEditColumn }: BoardColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${column.id}` })

  return (
    <section className="flex w-72 shrink-0 flex-col">
      <header className="mb-2 flex items-center gap-2 px-1">
        <h2 className="text-sm font-medium">{column.name}</h2>
        <Badge variant={column.over_wip_limit ? "destructive" : "secondary"}>
          {column.wip_limit ? `${column.tickets.length}/${column.wip_limit}` : column.tickets.length}
        </Badge>

        <div className="ml-auto flex items-center gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Add ticket to ${column.name}`}
              onClick={() => onAddTicket(column)}
            >
              <IconPlus className="size-4" />
            </Button>
          )}
          {canAdminister && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Column settings for ${column.name}`}
              onClick={() => onEditColumn(column)}
            >
              <IconSettings className="size-4" />
            </Button>
          )}
        </div>
      </header>

      <div
        ref={setNodeRef}
        className={cn(
          "bg-muted/40 flex min-h-32 flex-1 flex-col gap-2 rounded-lg p-2 transition-colors",
          isOver && "bg-accent",
        )}
      >
        <SortableContext items={column.tickets.map((ticket) => ticket.key)} strategy={verticalListSortingStrategy}>
          {column.tickets.map((ticket) => (
            <TicketCard key={ticket.key} ticket={ticket} draggable={canEdit} />
          ))}
        </SortableContext>

        {column.tickets.length === 0 && (
          <p className="text-muted-foreground p-2 text-center text-xs">Nothing here yet</p>
        )}
      </div>
    </section>
  )
}
